/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Form, Header, Icon, Label } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { getItemTypes } from '../../../services/ItemType/ItemType.service';
import UltyIndeterminateCheckboxGroup from '../../Shared/UltyIndeterminateCheckboxGroup/UltyIndeterminateCheckboxGroup';
import useUser from '../../Shared/UserProvider/useUser';
import { UltyInputText } from '@ulty/ulty-ui';
import AdvancedMenuFilters from './AdvancedMenuFilters';
import { isProviderAPos } from '../../../services/Provider/Provider.service';
import { ultyToast } from '../../Shared/UltyToast/UltyToast';

/* ------------------------------------------------------------------ *
 * Styles
 * ------------------------------------------------------------------ */

const filtersGrid = css`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr)) minmax(220px, 1.5fr);
  column-gap: 24px;
  row-gap: 20px;
  align-items: stretch;

  @media only screen and (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 18px;
    row-gap: 16px;
  }

  @media only screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    justify-items: center;
    padding: 0;
    row-gap: 0;

  }
`;

const filterGrid = css`
  ${filtersGrid};
  grid-template-columns: repeat(3, minmax(200px, 1fr));

  @media only screen and (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const filterGroup = css`
  min-width: 0;
  padding: 0 8px;

  & > div {
    margin: 0;
  }

  & input,
  & .ui.input > input,
  & .ui.selection.dropdown,
  & .ui.dropdown {
    border-radius: 0 !important;
  }

  @media only screen and (max-width: 1000px) {
    padding: 8px 0;
  }

  @media only screen and (max-width: 600px) {
    padding: 12px 0;
    width: min(100%, 30rem);
    margin-inline: auto;

    &:first-of-type {
      border-top: 0;
    }
  }
`;

const quantityGroup = css`
  ${filterGroup};
  grid-column: 3 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;

  & .ulty-threshold-tag {
    width: 100%;
    max-width: 200px;
    margin: 0;
    cursor: pointer;
    text-align: center;
  }

  @media only screen and (max-width: 1000px) {
    grid-column: 1 / -1;
  }
`;

const sectionHeader = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const footer = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 0.5px solid rgba(34, 36, 38, 0.1);
`;

const AdvancedFilters = ({ defaultFilters, onFiltersChange }) => {
  const { user } = useUser();
  const [t] = useTranslation();
  const [itemTypesFilterOptions, setItemTypesFilterOptions] = useState([]);

  const [measurementTypeFilterOptions, setMeasurementTypeFilterOptions] = useState(
    ['COUNT', 'WEIGHT', 'VOLUME'].map((type) => ({
      label: t(`global.enums.measurementTypes.${type}`),
      checked: defaultFilters.measurementUnitType !== undefined ? defaultFilters.measurementUnitType.includes(type) : true,
      value: type,
    }))
  );

  const [providerTypeFilterOptions, setProviderTypeFilterOptions] = useState([
    {
      label: t('provider.type_COMPANY'),
      checked: defaultFilters.providerType !== undefined ? defaultFilters.providerType === 'COMPANY' : true,
      value: 'COMPANY',
    },
    {
      label: t('provider.type_POINT_OF_SALE'),
      checked: defaultFilters.providerType !== undefined ? defaultFilters.providerType === 'POINT_OF_SALE' : true,
      value: 'POINT_OF_SALE',
    },
  ]);

  const [containsAlcoholFilterOptions, setContainsAlcoholFilterOptions] = useState([
    {
      label: t('product_list.advanced_filters.general.alcohol.with'),
      checked: defaultFilters.containsAlcohol !== undefined ? defaultFilters.containsAlcohol : true,
      value: true,
    },
    {
      label: t('product_list.advanced_filters.general.alcohol.without'),
      checked: defaultFilters.containsAlcohol !== undefined ? !defaultFilters.containsAlcohol : true,
      value: false,
    },
  ]);

  const [imageFilterOptions, setImageFilterOptions] = useState([
    {
      label: t('product_list.advanced_filters.general.image.with'),
      checked: defaultFilters.withImage !== undefined ? defaultFilters.withImage : true,
      value: true,
    },
    {
      label: t('product_list.advanced_filters.general.image.without'),
      checked: defaultFilters.withImage !== undefined ? !defaultFilters.withImage : true,
      value: false,
    },
  ]);

  const [unavailableFilterOptions, setUnavailableFilterOptions] = useState([
    {
      label: t('product_list.advanced_filters.inventory.availability.available'),
      checked: defaultFilters.unavailableUntil !== undefined ? !defaultFilters.unavailableUntil : true,
      value: false,
    },
    {
      label: t('product_list.advanced_filters.inventory.availability.unavailable'),
      checked: defaultFilters.unavailableUntil !== undefined ? defaultFilters.unavailableUntil : true,
      value: true,
    },
  ]);

  const [isUncountableFilterOptions, setIsUncountableFilterOptions] = useState([
      {
      label: t('product_list.advanced_filters.inventory.uncountable.with'),
      checked: defaultFilters.isUncountable !== undefined ? defaultFilters.isUncountable : true,
      value: true,
    },
    {
      label: t('product_list.advanced_filters.inventory.uncountable.without'),
      checked: defaultFilters.isUncountable !== undefined ? !defaultFilters.isUncountable : true,
      value: false,
    },
  ]);

  const [filters, setFilters] = useState(defaultFilters);

  const {
    data: itemTypes = [],
    isFetching: isLoading,
    isError: itemTypesError,
  } = useQuery({
    queryKey: ['item-types', 'advanced-filters'],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 10,
    select: (itemTypes) =>   itemTypes.map((type) => ({
        label: t(`global.enums.itemTypes.${type.name}`),
        checked: defaultFilters.types !== undefined ? defaultFilters.types.includes(type.name) : true,
        value: type.name,
      })),
    queryFn: () => getItemTypes(['PRODUCT', 'MENU', 'EXTRA']),
  });

  useEffect(() => {
    setItemTypesFilterOptions(
      itemTypes
    );
  }, [itemTypes]);

  useEffect(() => {
    if (itemTypesError) {
      ultyToast({ type: 'error', title: t('global.anErrorOccurred') });
    }
  }, [itemTypesError, t]);

  const syncCheckedOptions = (setter, checkedValues) => {
    setter((prev) =>
      prev.map((p) => ({
        ...p,
        checked: checkedValues.includes(p.value),
      }))
    );
  };

  const handleCheckboxGroupChange = (setter, onCheckedValuesChange) => ({ checkedValues }) => {
    syncCheckedOptions(setter, checkedValues);
    onCheckedValuesChange(checkedValues);
  };

  const handleExclusiveFilterChange = (filterName) => (selectedValues) => {
    setFilters(prev => {
      if (selectedValues.length === 1) {
        return { ...prev, [filterName]: selectedValues[0] };
      }
      const next = { ...prev };
      delete next[filterName];
      return next;
    });
  };

  const handleMeasurementTypeChange = (selectedValues) => {
    setFilters(prev => {
      if (selectedValues.length > 0 && selectedValues.length < 3) {
        return { ...prev, measurementUnitType: selectedValues };
      }
      const next = { ...prev };
      delete next.measurementUnitType;
      return next;
    });
  };

  const handleTypesChange = (selectedValues) => {
    setFilters(prev => {
      if (selectedValues.length === 0) {
        const next = { ...prev };
        delete next.types;
        return next;
      }
      return { ...prev, types: selectedValues };
    });
  };

  const handleMaxQuantityChange = (e) => {
    const value = e.target.value;
    setFilters(prev => {
      if (value === '') {
        const next = { ...prev };
        delete next.maxQuantity;
        return next;
      }
      return { ...prev, maxQuantity: value };
    });
  };

  const handleSetMaxQuantityToPopMinimumThreshold = () => {
    setFilters(prev => ({ ...prev, maxQuantity: user.provider.object.stockUnavailableThreshold }));
  };

  const handleFilters = () => {
    onFiltersChange(filters);
  };

  const RenderFilterCheckboxGroup = ({ labelKey, options, setOptions, onChange, extraProps }) => (
    <div css={filterGroup}>
      <UltyIndeterminateCheckboxGroup
        parentLabel={t(labelKey)}
        showParentCheckbox={false}
        asMobileButton
        options={options}
        onChange={handleCheckboxGroupChange(setOptions, onChange)}
        {...extraProps}
      />
    </div>
  );

  return (
    <Form
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <Divider horizontal>
        <span css={sectionHeader}>
          <Header
            as="h4"
            css={css`
              margin: 0 !important;
            `}
          >
            <Icon name="globe" color="teal" />
            {t('product_list.advanced_filters.general.general')}
          </Header>
        </span>
      </Divider>

      <div css={filterGrid}>
        <RenderFilterCheckboxGroup
          labelKey="product_list.advanced_filters.inventory.uncountable.uncountable"
          options={isUncountableFilterOptions}
          setOptions={setIsUncountableFilterOptions}
          onChange={handleExclusiveFilterChange('isUncountable')}
        />
        <RenderFilterCheckboxGroup
          labelKey="product_list.advanced_filters.general.alcohol.alcohol"
          options={containsAlcoholFilterOptions}
          setOptions={setContainsAlcoholFilterOptions}
          onChange={handleExclusiveFilterChange('containsAlcohol')}
        />
        <RenderFilterCheckboxGroup
          labelKey="product_list.advanced_filters.general.image.image"
          options={imageFilterOptions}
          setOptions={setImageFilterOptions}
          onChange={handleExclusiveFilterChange('withImage')}
        />
        <RenderFilterCheckboxGroup
          labelKey="product_list.advanced_filters.general.item_types"
          options={itemTypesFilterOptions}
          setOptions={setItemTypesFilterOptions}
          onChange={handleTypesChange}
          extraProps={{ loading: isLoading, asMobileButton: false }}
        />
        <RenderFilterCheckboxGroup
          labelKey="product_list.advanced_filters.general.measurement_type"
          options={measurementTypeFilterOptions}
          setOptions={setMeasurementTypeFilterOptions}
          onChange={handleMeasurementTypeChange}
          extraProps={{ asMobileButton: false }}
        />
        <div css={filterGroup}>
          <AdvancedMenuFilters filters={filters} setFilters={setFilters} />
        </div>
      </div>

      {isProviderAPos(user.provider) && (
        <>
          <Divider horizontal>
            <span css={sectionHeader}>
              <Header
                as="h4"
                css={css`
                  margin: 0 !important;
                `}
              >
                <Icon name="warehouse" color="teal" />
                {t('product_list.advanced_filters.inventory.pos')}
              </Header>
            </span>
          </Divider>

          <div css={filterGrid}>
            <RenderFilterCheckboxGroup
              labelKey="product_list.advanced_filters.general.provider_type"
              options={providerTypeFilterOptions}
              setOptions={setProviderTypeFilterOptions}
              onChange={handleExclusiveFilterChange('providerType')}
            />
            <RenderFilterCheckboxGroup
              labelKey="product_list.advanced_filters.inventory.availability.availability"
              options={unavailableFilterOptions}
              setOptions={setUnavailableFilterOptions}
              onChange={handleExclusiveFilterChange('unavailableUntil')}
            />

            <div css={quantityGroup}>
              <div
              >
                <UltyInputText
                  label={t('product_list.advanced_filters.inventory.quantity.available_quantity')}
                  type="number"
                  min={-1}
                  value={filters.maxQuantity ?? ''}
                  onChange={handleMaxQuantityChange}
                />
              </div>

              <Label
                as="a"
                color="teal"
                size="small"
                css={css`
                  font-size: 0.7rem !important;
                  min-width: 100%!important;
                  text-align: center;
                `}
                basic
                className="ulty-threshold-tag"
                onClick={handleSetMaxQuantityToPopMinimumThreshold}
                content={t('product_list.advanced_filters.inventory.quantity.pos_minimum_threshold')}
                detail={user.provider.object.stockUnavailableThreshold ?? 0}
              >
              </Label>
            </div>
          </div>
        </>
      )}

      <div css={footer}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          <Button color="teal" icon labelPosition="left" onClick={handleFilters}>
            <Icon name="check" />
            {t('global.validate')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default AdvancedFilters;